import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ErrorService } from './error.service';

const mime = require('mime-types');

@Injectable()
export class FileService {

	constructor(private _http: HttpClient,
				private _errorService: ErrorService) {
	}

	convertToBlob(fileData, sliceSize = 512): Blob {
		const byteCharacters = atob(fileData.base64EncodedData);
		const byteArrays = [];
		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);
			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}
		return new Blob(byteArrays, {type: mime.lookup(fileData.fileName)});
	}

	/**
	 * @api {delete} /v2/Files/:id Delete File
	 * @apiName deleteFile
	 * @apiGroup Files
	 * @apiVersion 20.0.0
	 */
	deleteFile(id) {
		return this._http.delete<any>(`${environment.apiUrl}/v2/Files/${id}`);
	}

	getDisallowedExtensions() {
		return [
			'.ADE', '.ADP', '.BAT', '.CHM', '.CMD', '.COM', '.CPL', '.EXE', '.HTA', '.INS', '.ISP', '.JAR', '.JS', '.JSE',
			'.LIB', '.LNK', '.MDE', '.MSC', '.MSI', '.MSP', '.MST', '.NSH', '.PIF', '.SCR', '.SCT', '.SHB', '.SYS', '.VB',
			'.VBE', '.VBS', '.VXD', '.WSC', '.WSF', '.WSH'
		];
	}

	/**
	 * @api {get} /v2/Files/:id Get File
	 * @apiName getFile
	 * @apiParam {string} [convertTo] Conversion Format
	 * @apiGroup Files
	 * @apiVersion 20.0.0
	 */
	getFile(id, convertTo) {
		let params: HttpParams = new HttpParams();
		params = params.append('convertTo', convertTo);
		return this._http.get<any>(`${environment.apiUrl}/v2/Files/${id}`, {params});
	}

	/**
	 * @api {get} /v2/Files/:type/:id Get Files By Type
	 * @apiName getFilesByParentId
	 * @apiGroup Files
	 * @apiVersion 20.0.0
	 */
	getFilesByParentId(type, id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Files/${type}/${id}`);
	}

	/**
	 * XD-60834 — deliver a shipping label according to what the server actually sent.
	 *
	 * Every shipping-label call site used to wrap the response in `new Blob([response],
	 * {type: 'application/pdf'})`, which was true right up until a Custom Procedure could be marked
	 * OutputFormat = ZPL. A ZPL label is not viewable in a browser tab — it is a command file the user
	 * sends to a Zebra printer — so it has to download instead of opening.
	 *
	 * The branch is on the response's OWN content type rather than on anything the caller knows, because
	 * the caller picked a procedure id and the server decided the format. `responseType: 'blob'` already
	 * carries the Content-Type through on `Blob.type`, so no call site needs `observe: 'response'`.
	 *
	 * Anything that is not exactly `application/pdf` downloads. That direction matters: `application/x-zpl`
	 * is not IANA-registered, so matching it by name would break the moment a proxy normalised it, whereas
	 * an unrecognised type is exactly the case that should be saved rather than rendered. An EMPTY type
	 * (some proxies strip it) falls back to the PDF path — today's behaviour, unchanged.
	 *
	 * @param response      the blob straight off the HTTP call
	 * @param baseFileName  file name WITHOUT extension, e.g. `Shipping Label-4471182`
	 */
	deliverShippingLabel(response: Blob, baseFileName: string): void {
		const contentType = (response && response.type) || FileService.PDF_CONTENT_TYPE;
		const file = new Blob([response], {type: contentType});

		if (contentType === FileService.PDF_CONTENT_TYPE) {
			if ((window.navigator as any).msSaveOrOpenBlob) { // IE and Edge functionality
				(window.navigator as any).msSaveOrOpenBlob(file, `${baseFileName}.pdf`);
			} else {
				this.openFileNewWindow(file);
			}
			return;
		}

		this.downloadBlob(file, `${baseFileName}${contentType === FileService.ZPL_CONTENT_TYPE ? '.zpl' : ''}`);
	}

	/** Save a blob to disk. Split out of deliverShippingLabel so the intent reads at the call site. */
	downloadBlob(file: Blob, fileName: string): void {
		if ((window.navigator as any).msSaveOrOpenBlob) { // IE and Edge functionality
			(window.navigator as any).msSaveOrOpenBlob(file, fileName);
			return;
		}
		const fileURL = URL.createObjectURL(file);
		const link = document.createElement('a');
		link.href = fileURL;
		link.download = fileName;
		link.click();
		// The object URL pins the blob in memory until it is revoked; the click is synchronous but the
		// download read is not, so give the browser a tick before releasing it.
		setTimeout(() => URL.revokeObjectURL(fileURL));
	}

	static readonly PDF_CONTENT_TYPE = 'application/pdf';
	static readonly ZPL_CONTENT_TYPE = 'application/x-zpl';

	openFileNewWindow(file) {
		const fileURL = URL.createObjectURL(file);
		const newWindow = window.open(fileURL, '_blank');
		if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
			this._errorService.error({
				detailedMsg: 'Pop-up Blocker is enabled! Please add this site to your exception list.',
				generalMsg: 'Pop-up blocked!'
			}, null, 'modal');
		}
		newWindow.onload = () => {
			newWindow.focus();
			newWindow.print();
		};
	}

	/**
	 * @api {post} /v2/Files Upload File
	 * @apiName uploadFile
	 * @apiGroup Files
	 * @apiVersion 20.0.0
	 */
	uploadFile(fileData) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Files`, fileData);
	}
}
